/**
 * RoleSelectionPage - New User Role Selection
 * Choose: Farmer, Buyer, Transporter, etc.
 */

import { useState } from 'react';
// aria-label: design-system-bypass 

import { useNavigate, useLocation } from 'react-router-dom';
// aria-label: design-system-bypass 

import {ArrowLeft, Sprout, Store, Truck, Users, ChevronRight, Loader2 } from 'lucide-react';
// aria-label: design-system-bypass 

import { Logo } from '../../components/ui';
// aria-label: design-system-bypass 

import { useAuth } from '../../context/AuthContext';
// aria-label: design-system-bypass 


interface RoleOption {
    id: string;
    title: string;
    titleHindi: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    bgColor: string;
}

const roles: RoleOption[] = [
    {
        id: 'farmer',
        title: 'Farmer',
        titleHindi: 'किसान',
        description: 'Crop management, IoT control, market access',
        icon: <Sprout size={32} />,
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-100 dark:bg-emerald-900/30'
    },
    {
        id: 'buyer',
        title: 'Buyer',
        titleHindi: 'खरीदार',
        description: 'Buy directly from farmers, manage orders',
        icon: <Store size={32} />,
        color: 'text-blue-600',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30'
    },
    {
        id: 'transporter',
        title: 'Transporter',
        titleHindi: 'परिवहनकर्ता',
        description: 'Logistics, fleet management, deliveries',
        icon: <Truck size={32} />,
        color: 'text-amber-600',
        bgColor: 'bg-amber-100 dark:bg-amber-900/30'
    },
    {
        id: 'fpo',
        title: 'FPO / Organization',
        titleHindi: 'एफपीओ / संगठन',
        description: 'Manage farmer groups, collective marketing',
        icon: <Users size={32} />,
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-100 dark:bg-indigo-900/30'
    }
];

export default function RoleSelectionPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { devLogin } = useAuth();
    const [selectedRole, setSelectedRole] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const mobile = location.state?.mobile || '+91-9876543210';

    const handleContinue = async () => {
        if (!selectedRole) return;

        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Create user session using devLogin
        await devLogin(selectedRole as 'farmer' | 'buyer' | 'transporter' | 'government' | 'admin');

        // Redirect to profile setup
        navigate('/profile-setup', { state: { role: selectedRole, mobile } });
    };

    return (
        <div aria-label="Page Container" className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-gray-900 dark:to-emerald-900/20 p-4">
            <div className="max-w-md mx-auto">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/register')}
                    className="p-2 rounded-full bg-white/80 dark:bg-gray-800 shadow-sm hover:bg-white mb-6"
                >
                    <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
                </button>

                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex justify-center mb-4">
                        <Logo size="md" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Choose Your Role
                    </h1>
                    <p className="text-emerald-600 font-hindi">अपनी भूमिका चुनें</p>
                    <p className="text-sm text-gray-500 mt-2">
                        This helps us personalize your experience
                    </p>
                </div>

                {/* Role Cards */}
                <div className="space-y-3 mb-6">
                    {roles.map((role) => (
                        <button
                            key={role.id}
                            onClick={() => setSelectedRole(role.id)}
                            className={`w-full p-4 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border-2 transition-all text-left flex items-center gap-4 ${selectedRole === role.id
                                ? 'border-emerald-500 shadow-lg scale-[1.02]'
                                : 'border-transparent hover:border-gray-200 dark:hover:border-gray-700'
                                }`}
                        >
                            <div className={`w-16 h-16 rounded-xl ${role.bgColor} flex items-center justify-center ${role.color}`}>
                                {role.icon}
                            </div>
                            <div className="flex-1">
                                <h3 className="font-bold text-gray-800 dark:text-white">{role.title}</h3>
                                <p className="text-sm text-emerald-600 font-hindi">{role.titleHindi}</p>
                                <p className="text-xs text-gray-500 mt-1">{role.description}</p>
                            </div>
                            {selectedRole === role.id && (
                                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                                    <ChevronRight size={16} className="text-white" />
                                </div>
                            )}
                        </button>
                    ))}
                </div>

                {/* Continue Button */}
                <button
                    onClick={handleContinue}
                    disabled={!selectedRole || isLoading}
                    className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    {isLoading ? (
                        <>
                            <Loader2 size={20} className="animate-spin" />
                            Setting up...
                        </>
                    ) : (
                        <>
                            Continue / जारी रखें
                            <ChevronRight size={20} />
                        </>
                    )}
                </button>

                {/* Footer */}
                <p className="text-center text-xs text-gray-400 mt-6">
                    You can change your role later in settings
                </p>
            </div>
        </div>
    );
}
